<script>
import appConfig from '@src/app.config'
import store from '@state/store'
import Layout from '@layouts/loggedin.vue'
import OrganizationHeader from '@organizations/components/organization-header.vue'
import PostFeed from '@router/components/post-feed.vue'
import PostFormWrapper from '@companies/components/post-form-wrapper.vue'
import EventTile from '@events/components/event-tile.vue'
import EventsSideBar from '@events/components/events-side-bar.vue'
import EventsFilter from '@companies/components/events-filter.vue'
import OrganizationEventsFilters from '@organizations/components/events-organization-filters.vue'
import EventBigCalendar from '@events/components/event-big-calendar.vue'
import EventDetails from '@events/components/event-details.vue'
import Tab from '@router/components/tab.vue'
import Tabs from '@router/components/tabs.vue'
import { authComputed } from '@state/helpers'
import { debounce } from 'debounce'
import { parseISO, isFuture, isPast, addMonths } from 'date-fns'
import { convertToCSV } from '@events/utils'
import EventsParentMixin from '@events/mixins/events-parent-mixin'

export default {
  components: {
    Layout,
    OrganizationHeader,
    PostFeed,
    PostFormWrapper,
    EventTile,
    EventsSideBar,
    EventsFilter,
    OrganizationEventsFilters,
    EventBigCalendar,
    EventDetails,
    Tab,
    Tabs,
  },
  mixins: [EventsParentMixin],
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loadingMore: false,
      noMorePosts: false,
      activeTab: 1,
      eventsPage: 0,
      eventsPerPage: 12,
      singleEvent: false,
      currentEventsTab: 'discover',
      coHostOrganizationId: this.organizationId,
    }
  },
  computed: {
    ...authComputed,
    kyndhubId() {
      return +appConfig.kyndhub_id
    },
    statusUpdates() {
      return this.$store.state.organizations.statusUpdates || []
    },
    organization() {
      return this.$store.state.organizations.organization || {}
    },
    organizationEvents() {
      return this.$store.state.organizations.organizationEvents || []
    },
    allCoHostEvents() {
      const events = this.$store.state.events.events
      if (!events || !Array.isArray(events)) return []
      if (!events.length) return events

      if (this.currentEventsTab === 'discover') {
        return events.filter(
          ({ end, ongoing }) => isFuture(parseISO(end)) || ongoing
        )
      }

      if (this.currentEventsTab === 'my') {
        return events.filter(
          ({ user_id: userId, leaders, users }) =>
            userId === this.currentUserId ||
            !!leaders.find(({ id }) => id === this.currentUserId) ||
            !!users.find(({ id }) => id === this.currentUserId)
        )
      }

      if (this.currentEventsTab === 'past') {
        return events.filter(({ end }) => isPast(parseISO(end)))
      }

      return events
    },
    coHostEvents() {
      const endIndex = (this.eventsPage + 1) * this.eventsPerPage
      return this.allCoHostEvents.slice(0, endIndex)
    },
    reachedLastEventPage() {
      return (
        Math.ceil(this.allCoHostEvents.length / this.eventsPerPage) -
          this.eventsPage -
          1 ===
        0
      )
    },
    canCreateEvent() {
      if (!this.currentUser || !this.currentUser.roles) {
        return false
      }

      const isSiteAdmin = this.currentUser.roles.find(
        (role) => role.name === 'site-admin'
      )

      const isOrganizationAdmin = this.currentUser.roles.find((role) =>
        role.name.startsWith('admin-of-organization-')
      )

      return !!(isSiteAdmin || isOrganizationAdmin)
    },
  },
  methods: {
    isFollowed() {
      var found = false
      // Check if organization is in user's organizations list
      if (this.currentUser.organizations) {
        for (
          let index = 0;
          index < this.currentUser.organizations.length;
          index++
        ) {
          if (
            this.currentUser.organizations[index].id === this.organizationId
          ) {
            found = true
          }
        }
      }
      return found
    },
    toggleFollowOrganization() {
      store
        .dispatch('interests/toggleFollowInterest', {
          interestId: this.organizationId,
          type: undefined,
          entity: 'Organization',
          isFollowed: this.isFollowed(),
        })
        .then(() => {})
        .catch(() => {})
    },
    fetchMoreStatusUpdates() {
      if (this.activeTab !== 1) return

      this.loadingMore = true
      store
        .dispatch('organizations/fetchStatusUpdates', {
          organizationId: this.organizationId,
        })
        .then((posts) => {
          this.loadingMore = false
          this.noMorePosts = !posts.length
        })
        .catch(() => {})
    },
    changedTab(params) {
      this.activeTab = params.index
      // When switching to Events tab, load co-host events
      if (params.index === 2) {
        this.loadCoHostEvents()
      }
    },
    loadCoHostEvents() {
      store.dispatch('events/fetchFilteredEvents', {
        coHostOrganizationId: this.organizationId,
        filters: {},
      })
      store.dispatch('events/fetchGroupsHierarchy')
    },
    currentEventsDate() {
      return this.$store.state.events.filters.from_date || new Date()
    },
    nextEventPage() {
      if (!this.reachedLastEventPage) {
        this.eventsPage++
      }
    },
    incrementMonth(by) {
      var newDate = addMonths(this.currentEventsDate(), by)
      store.dispatch('events/setFilterMonth', {
        coHostOrganizationId: this.organizationId,
        monthDate: newDate,
      })
      return newDate
    },
    openEventModal(oneEvent) {
      this.singleEvent = oneEvent
    },
    closeEventModal() {
      this.singleEvent = null
    },
    updateMonth: debounce(async function updateMonth(date) {
      await store.dispatch('events/setFilterMonth', {
        coHostOrganizationId: this.organizationId,
        monthDate: date,
      })
    }, 500),
    setEventsTab(tab) {
      this.currentEventsTab = tab
      this.eventsPage = 0
    },
    exportEvents() {
      const csvString = convertToCSV(this.allCoHostEvents)
      const fileName = `organization-${this.organizationId}-${this.currentEventsTab}-events.csv`
      if (window.navigator.msSaveOrOpenBlob) {
        const blob = new Blob([csvString])
        window.navigator.msSaveOrOpenBlob(blob, fileName)
      } else {
        const a = document.createElement('a')
        a.href = 'data:attachment/csv,' + encodeURIComponent(csvString)
        a.target = '_blank'
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        setTimeout(() => a.remove(), 2000)
      }
    },
  },
}
</script>

<template>
  <Layout>
    <OrganizationHeader
      :organization="organization"
      :organization-id="organizationId"
      :is-followed="isFollowed()"
      @toggle-follow="toggleFollowOrganization"
    />

    <!-- Organization Info Row -->
    <div class="bg-white px-15 py-15">
      <div class="flex">
        <!-- First Column: Mission -->
        <div class="w-1/2 pr-8">
          <h3 class="text-gray-light mb-2">Mission:</h3>
          <p class="text-gray-600">{{
            organization.mission_statement || 'No mission statement available.'
          }}</p>
        </div>

        <!-- Second Column: Split into 3 sub-columns -->
        <div class="w-1/2 flex">
          <!-- Website Column -->
          <div class="w-1/2 px-4">
            <h3 class="text-gray-light mb-2">Website:</h3>
            <a
              v-if="organization.main_website"
              :href="organization.main_website"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue hover:underline break-words"
            >
              {{ organization.main_website }}
            </a>
            <p v-else class="text-gray-400">N/A</p>
          </div>

          <!-- Social Column -->
          <div class="w-1/2 px-4">
            <h3 class="text-gray-light mb-2">Social:</h3>
            <div class="flex gap-3 flex-wrap">
              <a
                v-if="organization.facebook_link"
                :href="organization.facebook_link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue hover:opacity-75"
                title="Facebook"
              >
                <BaseIcon name="facebook" class="w-6 h-6" />
              </a>
              <a
                v-if="organization.x_link"
                :href="organization.x_link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue hover:opacity-75"
                title="X (Twitter)"
              >
                <BaseIcon name="twitter" class="w-6 h-6" />
              </a>
              <a
                v-if="organization.instagram_link"
                :href="organization.instagram_link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue hover:opacity-75"
                title="Instagram"
              >
                <BaseIcon name="instagram" class="w-6 h-6" />
              </a>
              <a
                v-if="organization.linkedin_link"
                :href="organization.linkedin_link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue hover:opacity-75"
                title="LinkedIn"
              >
                <BaseIcon name="linkedin" class="w-6 h-6" />
              </a>
              <a
                v-for="social in organization.custom_socials"
                :key="social.link"
                :href="social.link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue hover:opacity-75"
                :title="social.title"
              >
                <BaseIcon name="link" class="w-6 h-6" />
              </a>
              <p
                v-if="
                  !organization.facebook_link &&
                    !organization.x_link &&
                    !organization.instagram_link &&
                    !organization.linkedin_link &&
                    (!organization.custom_socials ||
                      !organization.custom_socials.length)
                "
                class="text-gray-400"
                >N/A</p
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="justify-between table pr-20 bg-white pl-15">
      <div class="z-10 table-cell align-top w-new-post">
        <PostFormWrapper :company-id="kyndhubId"> </PostFormWrapper>
        <div class="mt-5 text-xl">
          <span class="">Organization</span>
          <span class="breadcrumb-separator">></span>
          <span class="text-blue">{{ organization.name }}</span>
        </div>
      </div>
      <div class="table-cell w-full text-right">
        <div class="inline-block ml-auto mr-0">
          <router-link
            v-if="canCreateEvent"
            class="block w-48 h-12 mb-4 text-right button primary-button"
            :to="{
              name: 'createNewEvent',
              params: {
                companyId: kyndhubId,
                coHostOrganizationId: organizationId,
              },
            }"
          >
            + Create An Event
          </router-link>
          <router-link
            :to="{
              name: 'organizationEvents',
              params: { organizationId: organizationId },
            }"
            class="flex justify-between block w-48 h-12 mb-4 button primary-button"
            ><svg
              class="inline-block h-12 mr-2 align-middle"
              xmlns="http://www.w3.org/2000/svg"
              width="16.187"
              height="16.142"
              viewBox="0 0 16.187 16.142"
            >
              <defs></defs>
              <g transform="translate(17.622 0.071)">
                <path
                  fill="#FFFFFF"
                  d="M-1.673,14.693l-3.6-3.615a6.9,6.9,0,0,0-1.2-9.689,6.9,6.9,0,0,0-9.688,1.2,6.9,6.9,0,0,0,1.2,9.689,6.9,6.9,0,0,0,8.554-.051l3.6,3.611a.809.809,0,0,0,1.14,0h0A.809.809,0,0,0-1.673,14.693ZM-10.7,12.12a5.3,5.3,0,0,1-5.316-5.283,5.3,5.3,0,0,1,5.284-5.316A5.3,5.3,0,0,1-5.421,6.8v.019A5.3,5.3,0,0,1-10.7,12.12Z"
                />
              </g></svg
            ><span class="align-middle">Find An Event</span>
          </router-link>

          <EventsFilter :co-host-organization-id="organizationId" />
        </div>
      </div>
    </div>

    <div class="w-content h-auto flex bg-blue-lightest">
      <Tabs
        :classes="'flex justify-start'"
        :parent-classes="'w-content bg-white flex justify-start pl-15'"
        :tab-classes="
          'border-b-5 pb-5 text-xl mx-8 leading-loose cursor-pointer'
        "
        @changed-tab="changedTab"
      >
        <Tab
          title='<svg id="Group_254" data-name="Group 254" xmlns="http://www.w3.org/2000/svg" width="29.422" height="25.443" viewBox="0 0 29.422 25.443" class="inline-block align-middle mr-2"><path id="Path_113" data-name="Path 113" d="M35.848,14.5H15.673a2.493,2.493,0,0,0-2.508,2.508v1.075H11.408A2.493,2.493,0,0,0,8.9,20.592V37a2.92,2.92,0,0,0,2.759,2.938H35.382A2.952,2.952,0,0,0,38.321,37v-20A2.463,2.463,0,0,0,35.848,14.5ZM13.164,37.112a1.3,1.3,0,0,1-.43,1,1.561,1.561,0,0,1-1.039.394A1.466,1.466,0,0,1,10.333,37V20.592a1.056,1.056,0,0,1,1.075-1.075h1.756ZM36.923,37a1.514,1.514,0,0,1-1.505,1.505H14.239a5.076,5.076,0,0,0,.287-.645v-.072c.036-.072.036-.179.072-.287v-.072c0-.108.036-.215.036-.358V17.008a1.056,1.056,0,0,1,1.075-1.075H35.848a1.056,1.056,0,0,1,1.075,1.075Z" transform="translate(-8.9 -14.5)" fill="#069bd7"/><path id="Path_114" data-name="Path 114" d="M65.269,36.1H58.317a.717.717,0,0,0,0,1.433h6.952a.717.717,0,1,0,0-1.433Z" transform="translate(-40.148 -28.36)" fill="#069bd7"/><path id="Path_115" data-name="Path 115" d="M65.269,47.6H58.317a.717.717,0,0,0,0,1.433h6.952a.717.717,0,1,0,0-1.433Z" transform="translate(-40.148 -35.739)" fill="#069bd7"/><path id="Path_116" data-name="Path 116" d="M48.137,59.1H31.617a.717.717,0,1,0,0,1.433h16.52a.717.717,0,0,0,0-1.433Z" transform="translate(-23.016 -43.117)" fill="#069bd7"/><path id="Path_117" data-name="Path 117" d="M65.269,24.5H58.317a.717.717,0,0,0,0,1.433h6.952a.717.717,0,1,0,0-1.433Z" transform="translate(-40.148 -20.916)" fill="#069bd7"/><path id="Path_118" data-name="Path 118" d="M48.137,70.7H31.617a.717.717,0,1,0,0,1.433h16.52a.717.717,0,0,0,0-1.433Z" transform="translate(-23.016 -50.561)" fill="#069bd7"/><path id="Path_119" data-name="Path 119" d="M31.617,34.211h6.307a.719.719,0,0,0,.717-.717V25.217a.719.719,0,0,0-.717-.717H31.617a.719.719,0,0,0-.717.717v8.278A.719.719,0,0,0,31.617,34.211Zm.717-8.278h4.874v6.845H32.333Z" transform="translate(-23.016 -20.916)" fill="#069bd7"/></svg><span class="align-middle">Discover</span>'
        >
          <div class="h-auto flex pt-6 pr-20 pl-15 bg-blue-lightest">
            <div
              class="left-posts w-7/12 max-w-1.95xl bg-white rounded-xl mr-17 shadow-box overflow-hidden"
            >
              <PostFeed
                :status-updates="statusUpdates"
                :loading-more="loadingMore"
                :no-more-posts="noMorePosts"
                @fetch-more-status-updates="fetchMoreStatusUpdates"
              ></PostFeed>
            </div>
            <EventsSideBar
              :co-host-organization-id="organizationId"
            ></EventsSideBar>
          </div>
        </Tab>

        <Tab
          title='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 957.8" height="24" fill="#069BD7" class="inline-block align-middle mr-2"><title>Events Icon</title><path d="M975.44,151.77H783.87V46.68a24.56,24.56,0,0,0-49.08,0V151.82H265.21V46.68a24.56,24.56,0,0,0-49.08-2q0,1,0,2V151.82H24.56A24.56,24.56,0,0,0,0,176.34v778A24.56,24.56,0,0,0,24.56,978.9H975.44A24.56,24.56,0,0,0,1000,954.34h0v-778a24.57,24.57,0,0,0-24.56-24.57Zm-24.56,778H49.08V460.2h901.8Zm0-518.66H49.08V200.9h167v61.87a24.56,24.56,0,0,0,49.08,0V200.9H734.74v61.87a24.57,24.57,0,0,0,49.09,0V200.9h167Z" transform="translate(0 -21.1)"/></svg><span class="align-middle">Events</span>'
        >
          <div
            v-infinite-scroll="nextEventPage"
            infinite-scroll-disabled="reachedLastEventPage"
          >
            <div class="w-2/3 mt-7.7 pl-10 pr-12 relative">
              <EventBigCalendar
                :current-date="currentEventsDate()"
                :events="allCoHostEvents || []"
                @filter-between="filterBetweenDates"
              >
              </EventBigCalendar>
            </div>
            <OrganizationEventsFilters :organization-id="organizationId" />
            <div class="h-auto mt-17 my-7.7">
              <p v-if="!coHostEvents" class="px-11 post-content">Loading...</p>
              <p v-else-if="!coHostEvents.length" class="px-11 post-content">
                There are no events that match these search criteria.
              </p>
              <div v-else class="flex events-grid">
                <div
                  v-for="oneEvent in coHostEvents"
                  :key="oneEvent.id"
                  class="event-box-wrapper"
                >
                  <EventTile
                    :one-event="oneEvent"
                    @open-modal="openEventModal(oneEvent)"
                  ></EventTile>
                </div>
              </div>
            </div>

            <div class="flex mb-7.7 pl-10 pr-12 relative justify-between">
              <div class="mr-4">
                <button
                  class="text-lg text-blue-darker block focus:outline-none"
                  @click="updateMonth(incrementMonth(-1))"
                  >&lt; Back</button
                >
                <span class="text-sm text-blue-darker block">Previous</span>
              </div>
              <div>
                <button
                  class="text-lg text-blue-darker block focus:outline-none"
                  @click="updateMonth(incrementMonth(1))"
                  >Next &gt;</button
                >
                <span class="text-sm text-blue-darker block">Upcoming</span>
              </div>
            </div>

            <EventDetails
              v-if="singleEvent"
              :one-event="singleEvent"
              @close="closeEventModal"
            ></EventDetails>
          </div>
        </Tab>
      </Tabs>
    </div>
  </Layout>
</template>
